const KEY_PREFIX = 'tfp-updates:';
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

class TfpUpdates extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.pendingOpen = false;
    this.connectionVersion = (this.connectionVersion || 0) + 1;
    this.copyOperation = 0;
    this.copyOperations = new WeakMap();
    this.details = this.querySelector('[data-tfp-updates-details]');
    this.content = this.querySelector('[data-tfp-updates-content]');
    this.mount = this.querySelector('[data-tfp-updates-mount]');
    this.drawer = this.querySelector('[data-tfp-updates-drawer]');
    this.badge = this.querySelector('[data-tfp-updates-badge]');
    this.bell = this.querySelector('[data-tfp-updates-open]');
    if (this.labelledBell !== this.bell) {
      this.labelledBell = this.bell;
      this.bellBaseLabel = this.bell?.getAttribute('aria-label') || '';
    }
    this.badges = [...this.querySelectorAll('[data-tfp-updates-badge]')];
    if (this.bell) {
      const bellBadge = this.bell.querySelector('[data-tfp-updates-badge]');
      if (bellBadge && !this.badges.includes(bellBadge)) this.badges.push(bellBadge);
    }
    this.readState = this.dataset.tfpUpdatesPresentation === 'benefits' ? null : this.readStorage();
    this.validateCards();
    this.refreshBadge();
    this.configureCopyActions();
    const { signal } = this.abortController;
    this.details?.addEventListener('click', (event) => this.handleTrigger(event), { signal });
    if (this.bell && !this.details?.contains(this.bell)) this.bell.addEventListener('click', (event) => this.handleTrigger(event), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element) || (event.target !== this && !this.contains(event.target))) return;
      const selectedCard = event.target.closest('[data-tfp-updates-card]');
      void this.openEditorSelection(selectedCard);
    }, { signal });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.target instanceof Element && (event.target === this || this.contains(event.target))) {
        this.connectionVersion += 1;
        this.pendingOpen = false;
        void this.drawer?.close?.();
        if (this.details) this.details.open = false;
      }
    }, { signal });
    this.content?.addEventListener('click', (event) => this.handleContentClick(event), { signal });
    this.content?.addEventListener('toggle', (event) => this.handleDetailToggle(event), { capture: true, signal });
    document.addEventListener('visibilitychange', () => this.refreshExpiry(), { signal });
    document.addEventListener('visitorConsentCollected', () => this.refreshPrivacy(), { signal });
    const expiryDelay = this.nextExpiryDelay();
    if (expiryDelay) this.expiryTimer = window.setTimeout(() => this.refreshExpiry(), expiryDelay);
    this.drawer?.querySelector('dialog')?.addEventListener('close', () => {
      this.resetCopyActions(this.content);
      this.resetDetails();
      if (this.content && this.details) this.details.append(this.content);
      this.bell?.setAttribute('aria-controls', this.content?.id || '');
      this.bell?.removeAttribute('aria-haspopup');
      this.bell?.removeAttribute('aria-expanded');
    }, { signal });
  }

  disconnectedCallback() {
    this.connectionVersion = (this.connectionVersion || 0) + 1;
    this.copyOperation += 1;
    this.abortController?.abort();
    window.clearTimeout(this.expiryTimer);
    this.abortController = null;
  }

  validateCards() {
    if (window.Shopify?.designMode) return;
    const now = Date.now();
    const cards = [...this.querySelectorAll('[data-tfp-updates-card]')];
    const focusedCard = cards.find((card) => card.contains(document.activeElement)) || null;
    for (const card of cards) {
      const startsAt = card.dataset.startsAt || '';
      const expiresAt = card.dataset.expiresAt || '';
      const validTimestamp = (value) => !value || (ISO_UTC.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().replace('.000Z', 'Z') === value);
      const startsValid = validTimestamp(startsAt);
      const expiresValid = validTimestamp(expiresAt);
      const withinWindow = (!startsAt || Date.parse(startsAt) <= now) && (!expiresAt || Date.parse(expiresAt) > now);
      if (!startsValid || !expiresValid || !withinWindow) {
        if (card.open) this.resetDetail(card);
        card.remove();
      }
    }
    const empty = this.querySelector('[data-tfp-updates-empty]');
    if (empty) empty.hidden = this.querySelectorAll('[data-tfp-updates-card]').length > 0;
    if (focusedCard && !focusedCard.isConnected) this.restoreExpiryFocus(cards, focusedCard, empty);
  }

  restoreExpiryFocus(cards, focusedCard, empty) {
    const focusedIndex = cards.indexOf(focusedCard);
    const nextCard = cards.slice(focusedIndex + 1).find((card) => card.isConnected);
    const previousCard = cards.slice(0, focusedIndex).reverse().find((card) => card.isConnected);
    const summary = (nextCard || previousCard)?.querySelector('[data-tfp-update-summary]');
    if (summary instanceof HTMLElement) {
      summary.focus();
      return;
    }
    if (empty instanceof HTMLElement && !empty.hidden) {
      const previousTabindex = empty.getAttribute('tabindex');
      empty.setAttribute('tabindex', '-1');
      empty.focus();
      if (previousTabindex === null) empty.removeAttribute('tabindex');
      else empty.setAttribute('tabindex', previousTabindex);
      return;
    }
    this.drawer?.querySelector('.theme-drawer__close-button')?.focus();
  }

  nextExpiryDelay() {
    if (window.Shopify?.designMode) return 0;
    const values = [...this.querySelectorAll('[data-tfp-updates-card][data-expires-at]')]
      .map((card) => Date.parse(card.dataset.expiresAt || ''))
      .filter((time) => Number.isFinite(time) && time > Date.now());
    return values.length ? Math.min(Math.max(Math.min(...values) - Date.now(), 1000), 2147483647) : 0;
  }

  refreshExpiry() {
    if (document.visibilityState === 'hidden') return;
    this.validateCards();
    this.refreshBadge();
    window.clearTimeout(this.expiryTimer);
    const delay = this.nextExpiryDelay();
    if (delay) this.expiryTimer = window.setTimeout(() => this.refreshExpiry(), delay);
  }

  privacyAllowed() {
    try {
      if (window.Shopify?.designMode) return false;
      const privacy = window.Shopify?.customerPrivacy;
      return Boolean(privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed());
    } catch {
      return false;
    }
  }

  readStorage() {
    if (!this.privacyAllowed()) return null;
    try {
      const saved = JSON.parse(localStorage.getItem(`${KEY_PREFIX}${this.dataset.tfpUpdatesStorageKey || this.dataset.tfpUpdatesSection}`) || '[]');
      if (!Array.isArray(saved) || saved.some((key) => typeof key !== 'string')) return null;
      const current = new Set([...this.querySelectorAll('[data-tfp-updates-card]')].map((card) => card.dataset.updateKey));
      return new Set(saved.filter((key) => current.has(key)));
    } catch {
      return null;
    }
  }

  refreshPrivacy() {
    if (this.dataset.tfpUpdatesPresentation === 'benefits') { this.readState = null; this.refreshBadge(); return; }
    if (!this.privacyAllowed()) {
      this.readState = null;
      this.refreshBadge();
      for (const button of this.querySelectorAll('[data-tfp-updates-read]')) button.hidden = true;
      return;
    }
    if (!this.readState) this.readState = this.readStorage();
    this.refreshBadge();
  }

  writeStorage() {
    if (!this.readState || !this.privacyAllowed()) { this.readState = null; this.refreshBadge(); return; }
    try {
      localStorage.setItem(`${KEY_PREFIX}${this.dataset.tfpUpdatesStorageKey || this.dataset.tfpUpdatesSection}`, JSON.stringify([...this.readState]));
    } catch {
      this.readState = null;
      this.refreshBadge();
    }
  }

  refreshBadge() {
    this.refreshSingleBadge();
    for (const badge of this.badges || []) {
      if (badge === this.badge) continue;
      badge.hidden = this.badge.hidden;
      badge.textContent = this.badge.textContent;
      const label = this.badge.getAttribute('aria-label');
      if (label) badge.setAttribute('aria-label', label);
      else badge.removeAttribute('aria-label');
      badge.setAttribute('aria-hidden', this.dataset.tfpUpdatesPresentation === 'benefits' || badge.hidden ? 'true' : 'false');
    }
    this.refreshTriggerLabel();
  }

  refreshTriggerLabel() {
    if (!this.bell || !this.bellBaseLabel) return;
    if (this.dataset.tfpUpdatesPresentation === 'benefits') { this.bell.setAttribute('aria-label', this.bellBaseLabel); return; }
    const unreadLabel = !this.badge?.hidden ? this.badge.getAttribute('aria-label') : '';
    this.bell.setAttribute('aria-label', unreadLabel ? `${this.bellBaseLabel} — ${unreadLabel}` : this.bellBaseLabel);
  }

  refreshSingleBadge() {
    if (!this.badge) return;
    if (this.dataset.tfpUpdatesPresentation === 'benefits') {
      this.badge.hidden = this.dataset.showIndicatorDot === 'false' || this.querySelectorAll('[data-tfp-updates-card]').length === 0;
      this.badge.textContent = '';
      this.badge.setAttribute('aria-hidden', 'true');
      this.badge.removeAttribute('aria-label');
      return;
    }
    if (!this.readState) {
      this.badge.hidden = true;
      this.badge.textContent = '';
      this.badge.removeAttribute('aria-label');
      for (const card of this.querySelectorAll('[data-tfp-updates-card]')) card.removeAttribute('data-read');
      for (const button of this.querySelectorAll('[data-tfp-updates-read]')) button.hidden = true;
      return;
    }
    const cards = [...this.querySelectorAll('[data-tfp-updates-card]')];
    const unread = cards.filter((card) => !this.readState.has(card.dataset.updateKey)).length;
    this.badge.hidden = unread === 0;
    this.badge.setAttribute('aria-hidden', unread === 0 ? 'true' : 'false');
    this.badge.textContent = unread ? String(unread) : '';
    if (unread) {
      const template = this.badge.dataset.unreadTemplate || 'Unread updates: {{ count }}';
      this.badge.setAttribute('aria-label', template.replace('__COUNT__', String(unread)).replace('{{ count }}', String(unread)));
    } else this.badge.removeAttribute('aria-label');
    for (const card of cards) {
      const button = card.querySelector('[data-tfp-updates-read]');
      if (button) {
        button.hidden = false;
        if (this.readState.has(card.dataset.updateKey)) {
          card.dataset.read = 'true';
          button.textContent = button.dataset.readCompleteLabel || button.textContent;
          button.disabled = true;
        } else {
          card.dataset.read = 'false';
          button.textContent = button.dataset.readLabel || button.textContent;
          button.disabled = false;
        }
      }
    }
  }

  async handleTrigger(event) {
    if (!(event.target instanceof Element)) return;
    const openTrigger = event.target.closest('[data-tfp-updates-open]');
    const summary = event.target.closest('summary');
    if (!openTrigger && summary !== this.rootSummary()) return;
    event.preventDefault();
    if (this.pendingOpen) return;
    if (!this.details || !this.content || !this.mount || !this.drawer) return;
    this.pendingOpen = true;
    const version = this.connectionVersion;
    const trigger = openTrigger || this.rootSummary();
    try {
      await import('@theme/theme-drawer');
      if (!this.isConnected || version !== this.connectionVersion) return;
      if (typeof this.drawer.open !== 'function') throw new Error('Drawer unavailable');
      this.refreshExpiry();
      this.details.open = false;
      this.mount.replaceChildren(this.content);
      this.bell?.setAttribute('aria-controls', this.drawer.id);
      this.bell?.setAttribute('aria-haspopup', 'dialog');
      this.bell?.setAttribute('aria-expanded', 'true');
      await this.drawer.open({ returnFocus: trigger });
    } catch (error) {
      if (this.isConnected && version === this.connectionVersion) {
        this.details.append(this.content);
        this.bell?.setAttribute('aria-controls', this.content.id);
        this.bell?.removeAttribute('aria-haspopup');
        this.bell?.removeAttribute('aria-expanded');
        this.details.open = true;
        this.content.scrollIntoView?.({ block: 'nearest' });
      }
      console.warn('[tfp-updates] Drawer enhancement unavailable:', error);
    } finally {
      if (version === this.connectionVersion) this.pendingOpen = false;
    }
  }

  rootSummary() {
    return this.details ? [...this.details.children].find((child) => child.matches('summary')) || null : null;
  }

  async openEditorSelection(card) {
    await this.handleTrigger({ target: this.rootSummary(), preventDefault() {} });
    if (card?.isConnected) {
      if (card instanceof HTMLDetailsElement) this.openDetail(card);
      else card.querySelector('[data-tfp-update-summary]')?.focus();
    }
  }

  async handleContentClick(event) {
    const back = event.target instanceof Element ? event.target.closest('[data-tfp-update-back]') : null;
    if (back) {
      const card = back.closest('[data-tfp-updates-card]');
      if (card) {
        event.preventDefault();
        this.resetDetail(card, true);
      }
      return;
    }
    const copy = event.target instanceof Element ? event.target.closest('[data-tfp-update-copy]') : null;
    if (copy instanceof HTMLElement) {
      event.preventDefault();
      void this.copyCode(copy);
      return;
    }
    const newsletter = event.target instanceof Element ? event.target.closest('[data-tfp-update-newsletter]') : null;
    if (newsletter && await this.openNewsletter(event)) return;
    this.handleRead(event);
  }

  configureCopyActions() {
    const available = typeof globalThis.navigator?.clipboard?.writeText === 'function';
    for (const button of this.querySelectorAll('[data-tfp-update-copy]')) {
      button.hidden = !available;
      button.disabled = false;
      delete button.dataset.copyError;
      button.textContent = button.dataset.copyLabel || button.textContent;
    }
  }

  async copyCode(button) {
    if (button.disabled) return;
    const value = button.dataset.copyValue || '';
    if (!value || typeof globalThis.navigator?.clipboard?.writeText !== 'function') return;
    const feedback = button.closest('[data-tfp-update-code]')?.querySelector('[data-tfp-update-copy-feedback]');
    const version = this.connectionVersion;
    this.copyOperation += 1;
    const buttonOperation = {};
    this.copyOperations ||= new WeakMap();
    this.copyOperations.set(button, buttonOperation);
    button.disabled = true;
    delete button.dataset.copyError;
    try {
      await globalThis.navigator.clipboard.writeText(value);
      if (!this.isConnected || version !== this.connectionVersion || this.copyOperations.get(button) !== buttonOperation) return;
      button.textContent = button.dataset.copyCompleteLabel || button.dataset.copyLabel || button.textContent;
      if (feedback) feedback.textContent = button.dataset.copySuccess || '';
    } catch {
      if (!this.isConnected || version !== this.connectionVersion || this.copyOperations.get(button) !== buttonOperation) return;
      button.dataset.copyError = 'true';
      button.textContent = button.dataset.copyLabel || button.textContent;
      if (feedback) feedback.textContent = button.dataset.copyFailure || '';
    } finally {
      if (this.isConnected && version === this.connectionVersion && this.copyOperations.get(button) === buttonOperation) {
        this.copyOperations.delete(button);
        button.disabled = false;
      }
    }
  }

  async openNewsletter(event) {
    const popup = document.querySelector('tfp-newsletter-popup[data-enabled="true"]');
    if (!popup?.isConnected || typeof popup.openFrom !== 'function' || typeof this.drawer?.close !== 'function') return false;
    event.preventDefault();
    const version = this.connectionVersion;
    await this.drawer.close();
    if (!this.isConnected || version !== this.connectionVersion || !popup.isConnected || popup.dataset.enabled !== 'true' || typeof popup.openFrom !== 'function') return true;
    await popup.openFrom(this.bell);
    return true;
  }

  handleDetailToggle(event) {
    const card = event.target;
    if (!(card instanceof HTMLDetailsElement) || !card.matches('[data-tfp-updates-card]')) return;
    if (!card.open) {
      const back = card.querySelector('[data-tfp-update-back]');
      if (back) back.hidden = true;
      if (!this.content?.querySelector('[data-tfp-updates-card][open]')) this.content.dataset.detailOpen = 'false';
      this.resetCopyActions(card);
      return;
    }
    this.openDetail(card);
  }

  openDetail(card) {
    if (!(card instanceof HTMLDetailsElement) || !card.matches('[data-tfp-updates-card]')) return;
    for (const other of this.content?.querySelectorAll('[data-tfp-updates-card][open]') || []) {
      if (other !== card) this.resetDetail(other);
    }
    card.open = true;
    if (this.content) this.content.dataset.detailOpen = 'true';
    this.markCardRead(card);
    const back = card.querySelector('[data-tfp-update-back]');
    if (back) {
      back.hidden = false;
      back.focus();
    }
  }

  resetDetail(card, restoreFocus = false) {
    if (!(card instanceof HTMLDetailsElement)) return;
    card.open = false;
    const back = card.querySelector('[data-tfp-update-back]');
    if (back) back.hidden = true;
    if (this.content && !this.content.querySelector('[data-tfp-updates-card][open]')) this.content.dataset.detailOpen = 'false';
    this.resetCopyActions(card);
    if (restoreFocus) card.querySelector('[data-tfp-update-summary]')?.focus();
  }

  resetDetails() {
    for (const card of this.content?.querySelectorAll('[data-tfp-updates-card][open]') || []) this.resetDetail(card);
    if (this.content) this.content.dataset.detailOpen = 'false';
  }

  resetCopyActions(root = this) {
    this.copyOperation += 1;
    this.copyOperations ||= new WeakMap();
    for (const button of root.querySelectorAll('[data-tfp-update-copy]')) {
      this.copyOperations.delete(button);
      button.disabled = false;
      delete button.dataset.copyError;
      button.textContent = button.dataset.copyLabel || button.textContent;
      const feedback = button.closest('[data-tfp-update-code]')?.querySelector('[data-tfp-update-copy-feedback]');
      if (feedback) feedback.textContent = button.dataset.copyManual || '';
    }
  }

  markCardRead(card) {
    if (!this.readState || !this.privacyAllowed()) return;
    this.readState.add(card.dataset.updateKey);
    card.dataset.read = 'true';
    this.writeStorage();
    this.refreshBadge();
  }

  handleRead(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-tfp-updates-read]') : null;
    const card = button?.closest('[data-tfp-updates-card]');
    if (!card || !this.readState) return;
    this.readState.add(card.dataset.updateKey);
    card.dataset.read = 'true';
    if (button.dataset.readCompleteLabel) button.textContent = button.dataset.readCompleteLabel;
    this.writeStorage();
    this.refreshBadge();
  }
}

if (!customElements.get('tfp-updates')) customElements.define('tfp-updates', TfpUpdates);
